/**
 * speech.js — Envoltura fina sobre speechSynthesis.
 *
 * La Web Speech API tiene tres trampas conocidas y esta capa existe para
 * taparlas de una vez, en un solo lugar:
 *
 *  1. `getVoices()` devuelve [] en la primera llamada en Chrome — la lista se
 *     carga async y avisa por `voiceschanged`.
 *  2. `onend` a veces NO se dispara (pestaña que pasa a segundo plano,
 *     utterance cancelada por el sistema). Sin timeout de rescate, una cola
 *     secuencial se queda trabada para siempre.
 *  3. `speechSynthesis` puede existir sin voces instaladas (contenedores,
 *     algunos Linux): hay que tratarlo como no soportado.
 *
 * Y una cuarta, que no es del navegador sino del contenedor: adentro del
 * WebView de Android la API **no existe** (`window.speechSynthesis` es
 * `undefined`, verificado en un Xiaomi con la app instalada), así que ahí se
 * locuta con el motor del teléfono (`lib/nativeSpeech.js`). Ese desvío vive acá
 * y no en cada llamador: `useVoiceAnnouncements` y la tarjeta de ajustes siguen
 * llamando a `speakText` sin saber quién habla.
 */
import {
  isNativeSpeechAvailable,
  nativeCancelSpeech,
  nativeListVoices,
  nativeSetVoicePreferences,
  nativeSpeak,
} from "./nativeSpeech.js";
import { earconLeadMs } from "./earcons.js";
import { sortSpanishVoices } from "./spanishVoices.js";

/**
 * ¿Existe la Web Speech API en este contexto?
 *
 * Distinta de `isSpeechSupported()` y la diferencia importa: "se puede locutar"
 * (que en la app es cierto por el motor nativo) NO implica "hay un objeto
 * `speechSynthesis` al que hablarle". Confundirlas rompía `/perfil` entero en
 * el teléfono con `Cannot read properties of undefined (reading
 * 'addEventListener')` — la tarjeta de voz se suscribe a `voiceschanged` al
 * montar, y ahí no hay a qué suscribirse.
 */
function hasWebSpeech() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/**
 * Cuántas frases de la app hay encoladas o sonando en el sintetizador del
 * navegador.
 *
 * `speechSynthesis` es UNO por documento y acá lo usan dos: la cola de alertas
 * y el botón «Probar» de la tarjeta de ajustes. Saber si está ocupado es lo que
 * permite no reclamar una alerta que va a quedar esperando detrás de otra
 * frase, y no cancelar una frase ajena creyendo que se cancela la propia.
 */
let enVueloWeb = 0;
/**
 * De las de `enVueloWeb`, cuántas ya fueron dadas por perdidas por su rescate.
 *
 * Siguen contadas —el contador de arriba solo baja con el evento terminal del
 * navegador— pero la cola ya soltó su turno, así que a efectos de "¿hay alguna
 * frase viva que una cancelación se llevaría puesta?" no cuentan.
 */
let abandonadasWeb = 0;
/**
 * Se incrementa en cada `cancelSpeech()`.
 *
 * Cancelar pone el contador en cero de golpe, pero las frases que ya estaban en
 * vuelo siguen registradas y sus eventos terminales llegan DESPUÉS: sin esta
 * marca de tanda, cada uno restaba de nuevo y el contador se iba a negativo —
 * `isSpeechBusy()` quedaba en false para siempre y el corte de la frase tardía
 * no se aplicaba nunca.
 */
let tandaWeb = 0;

/**
 * ¿Hace falta ponerse de acuerdo con otras pestañas antes de locutar?
 *
 * La decisión de plataforma vive acá y no en quien llama: en la app hay UN
 * WebView, así que el registro compartido no tiene con quién competir y lo
 * único que cobra es la demora de la pestaña oculta —250 ms por alerta, y con
 * la pantalla apagada "oculta" es el caso normal— más un leer-modificar-
 * escribir del localStorage.
 */
export function needsCrossTabCoordination() {
  return !isNativeSpeechAvailable();
}

/**
 * Hasta cuándo se lo da por ocupado, aunque el contador siga arriba.
 *
 * El contador no baja hasta que el navegador entrega un evento terminal, y ésos
 * a veces no llegan nunca. Sin este vencimiento, una frase muda dejaba al
 * sintetizador marcado como ocupado y cada alerta siguiente de la ráfaga pagaba
 * el tope entero de `waitForSpeechIdle` antes de poder siquiera reclamar: una
 * ráfaga de diez segundos se volvía un minuto, con el silencio en el medio.
 */
let ocupadoHasta = 0;

/** ¿El sintetizador del navegador está diciendo algo nuestro? */
export function isSpeechBusy() {
  return enVueloWeb > 0 && Date.now() < ocupadoHasta;
}

/**
 * Espera a que el sintetizador quede libre.
 *
 * Lo usa la cola de alertas ANTES de reclamar el turno: la frase de prueba de
 * los ajustes no pasa por el registro compartido, así que una alerta encolada
 * detrás de ella empezaba a sonar más tarde que su propia marca provisional y
 * la pestaña que había cedido la repetía.
 *
 * El tope existe porque una frase que nunca reporta el final no puede dejar la
 * cola dormida: vencido, se sigue igual.
 */
export async function waitForSpeechIdle(maxMs = 15_000) {
  const hasta = Date.now() + maxMs;
  while (isSpeechBusy() && Date.now() < hasta) {
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

/** ¿Se puede locutar, por el camino que sea? */
export function isSpeechSupported() {
  return isNativeSpeechAvailable() || hasWebSpeech();
}

// ---------------------------------------------------------------------------
// Catálogo de voces del motor nativo
//
// El WebView no lo puede mirar solo, y `listVoices()` tiene que ser SÍNCRONA
// (la tarjeta de ajustes la llama en pleno render). Así que el catálogo se pide
// una vez al puente, se cachea acá y el aviso de "ya llegó" se emite por el
// mismo `onVoicesChanged` que usa la rama web — los llamadores no distinguen.
// ---------------------------------------------------------------------------

let nativeVoices = [];
let nativeVoicesPedidas = false;
const nativeVoiceListeners = new Set();

/**
 * Cuánto se espera al puente nativo antes de dar el catálogo por vacío.
 *
 * `NativeSpeech.getVoices()` resuelve desde el callback de arranque del motor,
 * y un motor de terceros que nunca llama a ese callback dejaba la promesa
 * colgada PARA SIEMPRE: el selector quedaba vacío el resto de la sesión, sin
 * reintento posible porque el pedido figuraba como en curso.
 */
const NATIVE_VOICES_TIMEOUT_MS = 8_000;

/**
 * Cada cuánto, como mucho, se vuelve a preguntar al volver a la app.
 *
 * Se pregunta SIEMPRE y no solo con el catálogo vacío: el usuario que ya tenía
 * una voz en español y va a instalar una segunda —una `es-CO` en un equipo que
 * solo traía `es-US`— vuelve a una lista que no cambió, y ahí el único camino
 * que le queda es matar la app. El plazo es lo único que evita un salto al
 * puente cada vez que la app pasa a primer plano.
 */
const NATIVE_VOICES_REFRESH_MS = 30_000;
let nativeVoicesPedidoAt = 0;
let nativeVoicesEnCurso = false;

function notificarVoces() {
  for (const handler of nativeVoiceListeners) {
    try {
      handler();
    } catch {
      // Un suscriptor que lanza no puede dejar a los demás sin enterarse: es el
      // mismo criterio que el reparto de mensajes del WebSocket.
    }
  }
}

/** Guarda el catálogo y avisa; `nativeVoicesPedidas` queda listo para reintentar. */
function guardarVoces(lista) {
  const nuevas = Array.isArray(lista) ? lista : [];
  // Un resultado VACÍO no pisa un catálogo que ya funcionaba: el puente puede
  // fallar un momento —el servicio de TTS del sistema se reconecta— y la
  // tarjeta pasaba a decir "este teléfono no tiene voces en español
  // instaladas" en un teléfono que sí las tiene. Se deja lo que había y se
  // marca para reintentar.
  if (nuevas.length === 0 && nativeVoices.length > 0) {
    nativeVoicesPedidas = false;
    return;
  }
  // Ojo: NO avisa. Quien llama decide cuándo, para que un refresco no dispare
  // dos avisos —uno acá y otro al terminar— y con ellos dos renders de la
  // tarjeta, cada uno releyendo el catálogo entero.
  nativeVoices = nuevas;
  // Un catálogo VACÍO no se da por bueno: el motor puede no haber terminado de
  // arrancar, o —el caso que importa— el usuario puede estar yendo a instalar
  // la voz justo ahora, que es lo que la tarjeta le dice que haga. Dejando el
  // pedido marcado como hecho, el selector se quedaba vacío el resto de la
  // carga y volver de los ajustes del sistema no cambiaba nada.
  nativeVoicesPedidas = nativeVoices.length > 0;
}

async function refreshNativeVoices() {
  nativeVoicesEnCurso = true;
  nativeVoicesPedidoAt = Date.now();
  // `nativeListVoices` ya devuelve [] ante cualquier fallo: no rechaza nunca.
  const pedido = nativeListVoices();

  let vencido = false;
  // El resultado TARDÍO no se tira. En un equipo lento el motor puede tardar
  // más que el límite de abajo, y sin esto la tarjeta se quedaba diciendo "este
  // teléfono no tiene voces en español instaladas" en un teléfono que sí las
  // tiene, hasta que alguien mandara la app a segundo plano y la trajera.
  //
  // La bandera la pone el límite, no el catálogo ya guardado: este handler
  // corre ANTES que el de la carrera —se registró primero— así que mirando
  // `nativeVoices` creía siempre que había llegado tarde y guardaba dos veces,
  // avisándole dos veces a cada suscriptor.
  pedido.then((tarde) => {
    if (!vencido || !Array.isArray(tarde) || tarde.length === 0) return;
    guardarVoces(tarde);
    notificarVoces();
  });

  let vencer;
  const limite = new Promise((resolve) => {
    vencer = setTimeout(() => {
      vencido = true;
      resolve([]);
    }, NATIVE_VOICES_TIMEOUT_MS);
  });
  try {
    guardarVoces(await Promise.race([pedido, limite]));
  } finally {
    clearTimeout(vencer);
    nativeVoicesEnCurso = false;
    notificarVoces();
  }
}

/**
 * ¿Todavía se le está preguntando al teléfono por sus voces?
 *
 * La tarjeta lo necesita para no afirmar lo que no sabe: con la lista vacía y
 * sin esto, mandaba al usuario a instalar voces —"Ajustes → Idiomas → Texto a
 * voz"— durante los cientos de ms (hasta 8 s) que tarda el puente, en un
 * teléfono que las tiene.
 */
export function isVoiceCatalogLoading() {
  if (isNativeSpeechAvailable()) return nativeVoicesEnCurso;
  // En web la carga también es async —`getVoices()` devuelve [] hasta que
  // dispara `voiceschanged`— y la tarjeta afirmaba "no hay voces en español
  // instaladas" mientras tanto, en un equipo que las tiene. Es el mismo
  // problema del teléfono, y hasta ahora el arreglo cubría un solo lado.
  //
  // El "todavía buscando" no puede durar para siempre: hay navegadores con
  // `speechSynthesis` y CERO voces (contenedores, algunos Linux) donde
  // `voiceschanged` no dispara nunca. Quien se suscribe arma un plazo que da el
  // catálogo por resuelto y avisa — ver `onVoicesChanged`.
  //
  // O sea que el plazo lo trae la SUSCRIPCIÓN: quien consulte esto sin
  // suscribirse se queda con "todavía buscando" para siempre en ese navegador.
  return hasWebSpeech() && !catalogoWebResuelto && listVoices().length === 0;
}

/** Solo para tests: vuelve a dejar el catálogo nativo sin pedir. */
export function __resetNativeVoices() {
  ordenadasWeb = undefined;
  // El enganche y el contador también: quedan atados al `speechSynthesis` que
  // el test anterior montó, y un contador que no vuelve a cero deja al
  // sintetizador marcado como ocupado para todos los tests siguientes.
  voiceschangedEnganchado = false;
  catalogoWebResuelto = false;
  enVueloWeb = 0;
  abandonadasWeb = 0;
  ocupadoHasta = 0;
  tandaWeb = 0;
  nativeVoicesEnCurso = false;
  nativeVoices = [];
  nativeVoicesPedidas = false;
  nativeVoicesPedidoAt = 0;
  nativeVoiceListeners.clear();
}

/**
 * Voces disponibles; puede venir vacío hasta que dispare `voiceschanged`.
 *
 * En la app son las del motor del teléfono, ya ordenadas por qué tan bien leen
 * español latino. En web, las del navegador, en el orden en que las lista.
 */
export function listVoices() {
  if (isNativeSpeechAvailable()) return nativeVoices;
  if (!hasWebSpeech()) return [];
  try {
    return window.speechSynthesis.getVoices() ?? [];
  } catch {
    return [];
  }
}

/**
 * Voces en español, de la más recomendable a la menos.
 *
 * El orden importa tanto como el filtro: la primera de esta lista es la misma
 * que se usa cuando el usuario no elige ninguna, así que el selector de la
 * tarjeta y el default no pueden discrepar.
 */
export function listSpanishVoices() {
  // El del teléfono ya viene ordenado por `SpanishVoices.java`, que puntúa con
  // dos señales que acá no existen: si la voz necesita red y la calidad que
  // declara el motor. Re-ordenarlo con el criterio de la web solo puede
  // romperlo — hoy empata todo y no se nota, pero el día que una etiqueta
  // cambie, la "Recomendada" de la tarjeta deja de ser la que el teléfono usa
  // con la app cerrada.
  if (isNativeSpeechAvailable()) return listVoices();
  return ordenadasDelNavegador();
}

/**
 * La voz que se usa cuando el usuario no eligió una, o `null` si el equipo no
 * tiene ninguna en español.
 *
 * Elegirla a mano no es un lujo: dejada al motor, la locución sale con la voz
 * POR DEFECTO DEL SISTEMA, que en la mayoría de los equipos es inglesa — y una
 * voz inglesa lee "Jesús" como "yísus" y la placa como ruido. Pedir
 * `lang: "es-CO"` tampoco alcanza: es una preferencia, no una orden, y los
 * motores caen igual a su voz por defecto cuando no tienen esa región exacta.
 */
export function recommendedVoice() {
  // La PRIMERA de la lista, no una cuenta aparte: la tarjeta muestra
  // `voices[0]` como "Recomendada — …", y dos implementaciones de un mismo
  // hecho terminan discrepando el día que el desempate de una cambie. En la app
  // además la lista la ordenó el motor del teléfono, con señales que
  // `pickSpanishVoice` no tiene.
  return listSpanishVoices()[0] ?? null;
}

/**
 * La voz concreta a usar: la que el usuario eligió si todavía existe, y si no
 * la recomendada.
 *
 * El respaldo es el que hace que la feature no se rompa sola: una voz elegida
 * puede desaparecer (otro equipo, otro navegador, el motor de TTS actualizado),
 * y caer a la voz del sistema por eso sería volver al acento inglés sin que
 * nadie toque nada.
 */
/**
 * La recomendada del NAVEGADOR, calculada una vez.
 *
 * `speechSynthesis.getVoices()` devuelve más de cien voces en un Chrome de
 * escritorio y puntuarlas es un regex por entrada: recalcularlo en cada frase
 * era cinco recorridos del catálogo en una ráfaga de cuatro alertas más su
 * resumen, en el hilo principal y mezclado con el earcon. La lista solo cambia
 * con `voiceschanged`, así que ahí se tira la cuenta.
 */
let ordenadasWeb;
let voiceschangedEnganchado = false;
/**
 * Cuánto se espera a que el navegador publique su catálogo antes de darlo por
 * vacío. Chrome lo puebla en decenas de ms; tres segundos es de sobra y evita
 * que la tarjeta se quede "buscando" eternamente donde no hay voces.
 */
const ESPERA_VOCES_WEB_MS = 3000;
/** ¿Ya se sabe qué voces tiene el navegador? Ver `isVoiceCatalogLoading`. */
let catalogoWebResuelto = false;

function ordenadasDelNavegador() {
  if (!voiceschangedEnganchado && hasWebSpeech() && window.speechSynthesis.addEventListener) {
    // La bandera se pone solo si el enganche OCURRIÓ: dándola por hecha en un
    // motor sin `addEventListener`, la cuenta quedaba cacheada sin nada que la
    // invalide nunca.
    voiceschangedEnganchado = true;
    window.speechSynthesis.addEventListener("voiceschanged", () => {
      ordenadasWeb = undefined;
      catalogoWebResuelto = true;
    });
  }
  // Una lista vacía solo se da por buena si hay quién la invalide: `getVoices()`
  // devuelve [] en la primera llamada de Chrome, y cachear ese vacío sin
  // enganche dejaba la alerta en manos de la voz del sistema —la inglesa— por
  // el resto de la carga de página. Con el enganche puesto, en cambio,
  // recalcular en cada frase es puntuar cien voces por alerta para llegar
  // siempre al mismo vacío.
  if (ordenadasWeb === undefined || (ordenadasWeb.length === 0 && !voiceschangedEnganchado)) {
    ordenadasWeb = sortSpanishVoices(listVoices());
    // Resuelto = ya se sabe qué hay. Una lista vacía CON enganche significa lo
    // contrario —la respuesta viene en camino, por eso se enganchó— y al revés
    // la tarjeta afirmaba "no hay voces en español" antes de que Chrome
    // terminara de cargar su catálogo.
    catalogoWebResuelto = ordenadasWeb.length > 0 || !voiceschangedEnganchado;
  }
  return ordenadasWeb;
}

function resolveVoice(voiceURI) {
  const elegida = voiceURI ? listVoices().find((v) => v.voiceURI === voiceURI) : null;
  // El respaldo pasa por `recommendedVoice`, no por otra cuenta propia: la
  // tarjeta muestra esa misma voz como "Recomendada — …" y la promesa es que
  // sea LA que se usa cuando el usuario no elige ninguna.
  return elegida ?? recommendedVoice();
}

/**
 * Suscribe a la carga async del catálogo de voces.
 *
 * En la app dispara el pedido al puente nativo la primera vez que alguien se
 * suscribe: el catálogo no se carga solo, y pedirlo al importar el módulo
 * costaría un salto al puente en cada arranque aunque nadie vaya a mirar la
 * lista.
 *
 * @returns {() => void} cleanup
 */
export function onVoicesChanged(handler) {
  if (isNativeSpeechAvailable()) {
    nativeVoiceListeners.add(handler);
    const pedirSiFalta = () => {
      if (nativeVoicesEnCurso || nativeVoicesPedidas) return;
      // A propósito SIN el plazo que sí tiene `volverAPedir`: un catálogo vacío
      // deja la bandera apagada para poder reintentar, y montar la tarjeta es
      // justo el gesto de quien fue a instalar la voz y volvió. El precio es un
      // salto al puente por montaje mientras no haya voces en español —una
      // lista vacía, no una alerta perdida— y el plazo lo pagaría el caso que
      // importa.
      nativeVoicesPedidas = true;
      refreshNativeVoices();
    };
    pedirSiFalta();

    const volverAPedir = () => {
      // Ni encima de uno en curso —su `finally` apagaría la bandera de "todavía
      // buscando" con el otro todavía esperando— ni antes del plazo.
      if (nativeVoicesEnCurso) return;
      if (Date.now() - nativeVoicesPedidoAt < NATIVE_VOICES_REFRESH_MS) return;
      nativeVoicesPedidas = true;
      refreshNativeVoices();
    };

    // Volver de los ajustes del teléfono no desmonta nada: la app se reanuda en
    // la MISMA carga de página. Sin esto, el usuario que va a instalar una voz
    // —lo que la propia tarjeta le dice que haga— volvía a encontrar el
    // selector igual de vacío, y la única salida era matar la app.
    if (typeof document === "undefined") return () => nativeVoiceListeners.delete(handler);
    const alVolver = () => {
      if (!document.hidden) volverAPedir();
    };
    document.addEventListener?.("visibilitychange", alVolver);
    return () => {
      nativeVoiceListeners.delete(handler);
      document.removeEventListener?.("visibilitychange", alVolver);
    };
  }
  if (!hasWebSpeech()) return () => {};
  const synth = window.speechSynthesis;
  synth.addEventListener?.("voiceschanged", handler);
  // El plazo corre desde que ALGUIEN mira, que es lo que hace comparable la
  // espera con lo que el usuario percibe. Vencido, el catálogo se da por
  // resuelto y se avisa: sin ese aviso, en un navegador sin voces el evento no
  // llega nunca y la tarjeta se quedaba en "Buscándolas…" para siempre, sin
  // nada que volviera a preguntar.
  const plazo = setTimeout(() => {
    // Solo si seguía sin resolverse: cuando el catálogo llegó a los 50 ms, este
    // aviso es un render de más de la tarjeta tres segundos después de abrirla,
    // por nada.
    if (catalogoWebResuelto) return;
    catalogoWebResuelto = true;
    handler();
  }, ESPERA_VOCES_WEB_MS);
  return () => {
    clearTimeout(plazo);
    synth.removeEventListener?.("voiceschanged", handler);
  };
}

/**
 * Ceba el sintetizador dentro de un gesto del usuario.
 *
 * En móvil `speechSynthesis.speak()` sin activación previa del usuario se
 * descarta EN SILENCIO — no lanza, no dispara `onerror`, simplemente no suena.
 * Y la activación es por documento: vale para la carga de página actual y se
 * pierde en la siguiente. Una locución muda dentro del primer gesto deja el
 * motor habilitado para las alertas que lleguen después, que es justo lo que
 * nunca pasaba al reabrir la app con la voz ya activada.
 *
 * @returns {boolean} true si se pudo encolar la locución de cebado
 */
export function primeSpeech() {
  // El motor nativo no arrastra la política de autoplay: es una app, no una
  // pestaña. No hay nada que cebar.
  if (isNativeSpeechAvailable()) return true;
  if (!hasWebSpeech()) return false;
  try {
    const synth = window.speechSynthesis;
    // Si ya hay algo sonando el motor está claramente desbloqueado, y encolar
    // acá solo agregaría una pausa muda en medio de una alerta real.
    if (synth.speaking || synth.pending) return false;
    const utterance = new window.SpeechSynthesisUtterance(" ");
    utterance.volume = 0;
    synth.speak(utterance);
    return true;
  } catch {
    return false;
  }
}

/**
 * Publica las preferencias de voz al motor que locuta con la app CERRADA.
 *
 * En web no hay nada que publicar: con la pestaña cerrada no habla nadie. En la
 * app sí, y esa locución no puede leer el `localStorage` del WebView porque el
 * WebView ni siquiera está corriendo — por eso las preferencias se copian del
 * lado nativo. Sin esta copia, el switch de la tarjeta, la lista por tipo, el
 * selector "cómo se identifica el vehículo" y el deletreo de la placa quedaban
 * decorativos justo con el teléfono bloqueado.
 *
 * @param {object} prefs
 * @param {boolean} prefs.enabled
 * @param {string} prefs.identify
 * @param {boolean} prefs.phonetic
 * @param {number} prefs.rate
 * @param {boolean} prefs.earcons
 * @param {string|null} prefs.voiceURI
 * @param {Record<string, boolean>} prefs.types
 */
export function publishVoicePreferences({
  enabled,
  identify,
  phonetic,
  rate,
  earcons,
  voiceURI,
  types,
}) {
  if (!isNativeSpeechAvailable()) return;
  nativeSetVoicePreferences({ enabled, identify, phonetic, rate, earcons, voiceURI, types });
}

export function cancelSpeech() {
  // Cancelar es soltar el sintetizador: si no, un `onend` que el navegador no
  // entrega (trampa conocida, ver el encabezado) lo dejaba marcado como ocupado
  // y la alerta siguiente esperaba en `waitForSpeechIdle` por una frase que ya
  // nadie está diciendo.
  enVueloWeb = 0;
  // Las abandonadas se cuentan sobre las de `enVueloWeb`: con el contador en
  // cero, su cuenta aparte tiene que irse con él o queda en negativo.
  abandonadasWeb = 0;
  ocupadoHasta = 0;
  tandaWeb += 1;
  if (isNativeSpeechAvailable()) {
    nativeCancelSpeech();
    return;
  }
  if (!hasWebSpeech()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* no-op */
  }
}

/**
 * En qué "tanda" de locución va la pestaña: sube con cada `cancelSpeech()`.
 *
 * Lo usa quien necesita distinguir una frase que TERMINÓ de una que se cortó:
 * `onend` dispara para las dos —incluso para una que nunca llegó a sonar,
 * cancelada mientras esperaba su turno en el sintetizador— y tratar la
 * cancelada como dicha dejaba la marca firme dos minutos, con lo que ninguna
 * pestaña volvía a decir una alerta que nadie escuchó.
 */
export function speechCancelEpoch() {
  return tandaWeb;
}

// Margen sobre la duración estimada antes de dar por perdida una locución.
const WATCHDOG_SLACK_MS = 4000;

/**
 * Cuánto hay que reservar el turno de una frase, en ms.
 *
 * Es el mismo número que el rescate por tiempo, y esa igualdad es el punto: la
 * reclamación tiene que seguir viva mientras esta pestaña pueda estar hablando.
 * Con un plazo fijo, una frase larga —una placa deletreada con el nombre del
 * conductor— la sobrevivía, y en un motor que no reporta `onstart` (WebKit, y
 * algunos Chrome de Android) la marca no llegaba a pasar a firme antes de
 * vencer: la pestaña que había cedido el turno lo tomaba y hablaba encima.
 *
 * El tono entra en la cuenta porque el turno se reclama ANTES de tocarlo: entre
 * la reclamación y la primera palabra hay un tono de hasta medio segundo más su
 * pausa, y sin sumarlos el plazo se consumía antes de que la frase arrancara.
 * `voiceLedger` igual le pone el piso de `PROVISIONAL_TTL_MS`; esto es para que
 * el número diga la verdad en vez de depender del piso.
 */
export function speechClaimMs(text, rate = DEFAULT_RATE, { earcon = false, alertType = null } = {}) {
  const tono = earcon ? earconLeadMs(alertType) : 0;
  return tono + estimateSpeechMs(text ?? "", rate) + WATCHDOG_SLACK_MS;
}
/**
 * Cuánto se espera un evento del navegador antes de dar la frase por ida.
 *
 * Es mucho más largo que el rescate a propósito: el rescate solo libera la
 * cola, mientras que esto decide si el sintetizador sigue ocupado, y darlo por
 * libre de más deja reclamar una alerta que va a quedar encolada.
 */
const OLVIDO_SLACK_MS = 60_000;

/**
 * "Normal" en la tarjeta (`DEFAULT_VOICE_PREFS.rate`, y `DEFAULT_RATE` del lado
 * Java). 1.0 dejó de ser un valor de la escala cuando bajó un escalón: una
 * llamada que omita `rate` no puede hablar a una velocidad que el selector no
 * sabe mostrar.
 */
export const DEFAULT_RATE = 0.9;
// Estimación grosera: ~12 caracteres por segundo a velocidad 1.
const CHARS_PER_SECOND = 12;

/** Cuánto se estima que tarda en decirse una frase, en ms. */
function estimateSpeechMs(text, rate) {
  return (text.length / CHARS_PER_SECOND / Math.max(rate, 0.1)) * 1000;
}

/**
 * Locución por el motor del teléfono.
 *
 * Dos cosas que no son obvias y que hay que hacer acá:
 *
 * 1. **Con la app en segundo plano no habla el frontend.** Ahí `AlertPresenter`
 *    ya locuta la misma alerta —solo se calla mientras la app está adelante— y
 *    el WebView sigue vivo recibiendo el evento por WebSocket. Sin esta guarda
 *    la frase se decía DOS veces seguidas: el espejo exacto de la guarda
 *    `isAppInForeground()` del lado nativo.
 *
 * 2. **Esperar lo que dura la frase.** `NativeSpeech.speak` resuelve al
 *    ENCOLAR, no al terminar: sin la espera, la cola de `useVoiceAnnouncements`
 *    se vacía de golpe, su tope de 4 no llega a aplicarse nunca y una ráfaga de
 *    30 geocercas encola 30 frases seguidas. No se puede resolver de verdad al
 *    final porque `AlertSpeaker` no reporta el fin de una locución concreta;
 *    la estimación es la misma que usa el watchdog de la rama web, más el
 *    tono y su pausa cuando el motor los toca delante de la frase.
 */
async function speakNative({ text, rate, urgent, voiceURI, earcon, alertType, onStart }) {
  if (typeof document !== "undefined" && document.hidden) return false;

  if (!(await nativeSpeak({ text, rate, urgent, voiceURI, earcon, alertType }))) return false;

  // El puente no reporta el inicio real, pero que `AlertSpeaker` haya aceptado
  // la frase es el compromiso equivalente: tiene wake lock, foco de audio y
  // cola propios, así que a partir de acá va a sonar.
  onStart?.();

  const tonoMs = earcon ? earconLeadMs(alertType) : 0;
  await new Promise((resolve) =>
    setTimeout(resolve, tonoMs + estimateSpeechMs(text, rate)),
  );
  return true;
}

/**
 * Locuta un texto y resuelve cuando terminó (o cuando el watchdog se cansa).
 *
 * Nunca rechaza: un fallo del sintetizador no puede romper la cola de alertas.
 *
 * @param {object} args
 * @param {string} args.text
 * @param {string|null} [args.voiceURI] voiceURI elegido por el usuario. Sin
 *   esto NO se cae a la voz del sistema —que suele ser inglesa— sino a la mejor
 *   voz en español instalada (ver `recommendedVoice`).
 * @param {number} [args.rate] 0.5..2
 * @param {number} [args.volume] 0..1
 * @param {boolean} [args.urgent] solo pesa en nativo: elige el canal de audio
 *   (alarma vs notificación). En web el volumen lo pone `volume`.
 * @param {boolean} [args.earcon] solo pesa en nativo: pide el tono corto antes
 *   de la frase. En web lo toca el llamador con `playEarcon`, que ahí sí tiene
 *   un AudioContext utilizable.
 * @param {string|null} [args.alertType] de qué alerta se trata, para que el
 *   tono nativo sea el de su familia.
 * @param {() => void} [args.onStart] se llama cuando la voz EMPEZÓ a sonar, no
 *   cuando terminó. **En la app significa "el motor la ACEPTÓ"**: el puente
 *   resuelve al encolar y el arranque real lo reporta `UtteranceProgressListener`
 *   del lado Java, que no cruza al WebView. Alcanza porque ahí no hay
 *   coordinación entre pestañas que dependa de la diferencia (ver
 *   `needsCrossTabCoordination`); el día que la haya, hay que subir esa señal. Es la única señal que distingue "no habló" de "habló pero
 *   no reportó el final": el watchdog de abajo no puede, y quien coordina entre
 *   pestañas necesita saberlo antes de que la frase termine.
 * @returns {Promise<boolean>} true si el navegador llegó a locutar
 */
export function speakText({
  text,
  voiceURI = null,
  rate = DEFAULT_RATE,
  volume = 1,
  urgent = true,
  earcon = false,
  alertType = null,
  onStart,
}) {
  if (!text) return Promise.resolve(false);

  // En la app, el motor del teléfono. Ignora `volume`: el volumen lo pone el
  // sistema, que es también lo que hace que la locución se oiga con la pantalla
  // bloqueada.
  if (isNativeSpeechAvailable()) {
    return speakNative({ text, rate, urgent, voiceURI, earcon, alertType, onStart });
  }

  if (!hasWebSpeech()) return Promise.resolve(false);

  return new Promise((resolve) => {
    let done = false;
    let watchdog;
    let olvido;
    let contada = false;
    // ¿Esta frase ya fue dada por perdida y sigue contada? Se lleva también en
    // un contador global (`abandonadasWeb`) para poder preguntar por el resto.
    let abandonadaWeb = false;
    const miTanda = tandaWeb;
    // El contador NO se baja al resolver la promesa: el rescate resuelve
    // `false` con la frase todavía encolada en el navegador, y ahí el
    // sintetizador sigue ocupado. Se baja con el evento terminal del navegador
    // —o, si nunca llega, con el olvido de abajo.
    const descontar = () => {
      if (!contada) return;
      contada = false;
      clearTimeout(olvido);
      const eraAbandonada = abandonadaWeb;
      abandonadaWeb = false;
      // Una cancelación posterior ya puso los DOS contadores en cero por todas
      // las frases de su tanda, ésta incluida: restarles de nuevo los hunde. La
      // marca local sí se limpia siempre (arriba), porque es de esta frase.
      //
      // La guarda vale igual para las abandonadas: el `olvido` de una frase
      // vieja llega un minuto tarde, y sin esto le descontaba a una frase de la
      // tanda nueva — con `abandonadasWeb` corta, la cancelación del `onstart`
      // tardío dejaba de dispararse y la frase vieja hablaba encima.
      if (miTanda !== tandaWeb) return;
      if (eraAbandonada) abandonadasWeb = Math.max(abandonadasWeb - 1, 0);
      enVueloWeb -= 1;
      if (enVueloWeb === 0) ocupadoHasta = 0;
    };
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(watchdog);
      // Una frase que la cola YA dio por perdida sigue contada en `enVueloWeb`
      // —el contador solo baja con el evento terminal del navegador—, así que
      // hay que llevarlas aparte: sin esto, "¿queda alguna frase viva?" daba
      // que sí por culpa de las abandonadas, que es justo al revés.
      //
      // La guarda de tanda va acá TAMBIÉN, y no solo en el decremento: una
      // cancelación pone los dos contadores en cero, así que sumarle a una
      // tanda que ya no existe deja un fantasma que nadie baja nunca — el
      // decremento sale por la guarda un minuto después, con el `olvido`. Con
      // el fantasma, `enVueloWeb - abandonadasWeb` cae por debajo de las frases
      // vivas y el `onstart` tardío cancela una alerta que SÍ tenía su turno.
      if (!value && contada && miTanda === tandaWeb) {
        abandonadaWeb = true;
        abandonadasWeb += 1;
      }
      resolve(value);
    };

    let utterance;
    try {
      utterance = new window.SpeechSynthesisUtterance(text);
    } catch {
      return resolve(false);
    }

    utterance.lang = "es-CO";
    utterance.rate = rate;
    utterance.volume = volume;

    const voice = resolveVoice(voiceURI);
    if (voice) {
      utterance.voice = voice;
      // La voz manda sobre el lang pedido: forzar es-CO sobre una voz es-ES
      // hace que algunos motores caigan a la voz por defecto del sistema.
      utterance.lang = voice.lang;
    }

    const rescateMs = estimateSpeechMs(text, rate) + WATCHDOG_SLACK_MS;

    utterance.onstart = () => {
      if (done) {
        // El rescate ya se cansó de esperar, así que quien coordina entre
        // pestañas SOLTÓ el turno y otra pestaña puede estar diciendo esta
        // alerta ahora mismo. Esta frase llega tarde y no tiene que sonar.
        //
        // `cancel` corta lo que suena EN ESTE INSTANTE —que es ella, su
        // `onstart` acaba de dispararse— pero vacía la cola ENTERA, así que
        // solo se usa cuando no queda ninguna frase VIVA: llevarse puesta la
        // alerta siguiente, que sí tiene su turno, sería peor que dejar sonar
        // ésta. Cancelar al vencer el rescate, en cambio, nunca apuntaba a la
        // frase correcta.
        //
        // Vivas son las contadas menos las abandonadas: preguntar por
        // `enVueloWeb === 1` daba 2 en el caso que este guardia existe para
        // atrapar —la demo de «Probar» adelante y esta alerta abandonada
        // detrás— y la frase tardía terminaba hablando encima de la siguiente.
        if (enVueloWeb - abandonadasWeb <= 0) {
          try {
            window.speechSynthesis.cancel();
          } catch {
            /* el sintetizador ya no está: nada que cancelar */
          }
        }
        return;
      }
      // La ocupación se mueve con el arranque REAL: fijada en `speak()`, una
      // frase que arranca tarde se daba por terminada mientras seguía hablando,
      // y la cola reclamaba la siguiente para encolarla detrás.
      ocupadoHasta = Math.max(ocupadoHasta, Date.now() + rescateMs);
      // Y el olvido con ella: anclado en `speak()`, a una frase que arranca muy
      // tarde le bajaba el contador en mitad de la locución, y la cola se creía
      // libre para encolar la siguiente encima.
      clearTimeout(olvido);
      olvido = setTimeout(descontar, rescateMs + OLVIDO_SLACK_MS);
      // Y el reloj del rescate arranca recién acá. `speechSynthesis` tiene UNA
      // cola global para todo el documento: entre `speak()` y el primer sonido
      // puede haber otra frase por delante, o la pausa de Chrome, y el rescate
      // contado desde `speak()` vencía con la frase todavía sin empezar.
      clearTimeout(watchdog);
      watchdog = setTimeout(() => finish(false), rescateMs);
      onStart?.();
    };
    utterance.onend = () => {
      descontar();
      finish(true);
    };
    utterance.onerror = () => {
      descontar();
      finish(false);
    };

    // Rescate: si `onend` nunca llega, la cola debe seguir igual.
    watchdog = setTimeout(() => finish(false), rescateMs);

    try {
      contada = true;
      enVueloWeb += 1;
      // Ocupado, como mucho, hasta donde llega el rescate de ESTA frase: pasado
      // ese punto la cola ya siguió sin ella y esperarla es esperar de más.
      ocupadoHasta = Math.max(ocupadoHasta, Date.now() + rescateMs);
      // Red de contención del contador: un navegador que no entrega NINGÚN
      // evento para esta frase no puede dejar al sintetizador marcado como
      // ocupado para siempre.
      olvido = setTimeout(descontar, rescateMs + OLVIDO_SLACK_MS);
      window.speechSynthesis.speak(utterance);
    } catch {
      descontar();
      finish(false);
    }
  });
}
